import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Briefcase, FileText, TrendingUp, CheckCircle, Target, Sparkles, Users, ArrowRight } from 'lucide-react';

const VendorMarketplaceSection = ({ onRegisterClick }) => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-gradient-to-b from-white to-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Briefcase className="w-4 h-4" />
            <span>{t('landing.vendorMarketplace.badge')}</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {t('landing.vendorMarketplace.title')}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t('landing.vendorMarketplace.subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-12 border border-slate-200">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Marketplace Benefits */}
            <div className="space-y-8">
              <div>
                <h3 className="text-3xl font-bold text-slate-900 mb-4">
                  {t('landing.vendorMarketplace.twoWays')}
                </h3>
                <p className="text-lg text-slate-600 mb-8">
                  {t('landing.vendorMarketplace.twoWaysSubtitle')}
                </p>
              </div>

              <div className="space-y-6">
                {/* One-time purchase */}
                <div className="flex items-start space-x-4 p-6 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xl font-bold text-slate-900">
                        {t('landing.vendorMarketplace.payPerRfp.title')}
                      </h4>
                      <span className="text-2xl font-bold text-green-600">
                        {t('landing.vendorMarketplace.payPerRfp.price')}
                      </span>
                    </div>
                    <p className="text-slate-600 mb-3">
                      {t('landing.vendorMarketplace.payPerRfp.description')}
                    </p>
                    <ul className="space-y-2">
                      {['feature1', 'feature2', 'feature3'].map((f) => (
                        <li key={f} className="flex items-center space-x-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>{t(`landing.vendorMarketplace.payPerRfp.${f}`)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Subscription */}
                <div className="flex items-start space-x-4 p-6 bg-gradient-to-br from-secondary to-secondary rounded-xl border-2 border-border">
                  <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xl font-bold text-slate-900">
                        {t('landing.vendorMarketplace.subscription.title')}
                      </h4>
                      <span className="text-sm font-semibold text-primary bg-secondary px-3 py-1 rounded-full">
                        {t('landing.vendorMarketplace.subscription.badge')}
                      </span>
                    </div>
                    <p className="text-slate-600 mb-3">
                      {t('landing.vendorMarketplace.subscription.description')}
                    </p>
                    <ul className="space-y-2">
                      {['free', 'starter', 'professional', 'enterprise'].map((plan) => (
                        <li key={plan} className="flex items-center space-x-2 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                          <span>
                            {(() => {
                              const text = t(`landing.vendorMarketplace.subscription.${plan}`);
                              const colonIdx = text.indexOf(':');
                              if (colonIdx === -1) return text;
                              return <><strong>{text.slice(0, colonIdx)}</strong>{text.slice(colonIdx)}</>;
                            })()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  size="lg"
                  onClick={onRegisterClick}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-stripe"
                >
                  {t('landing.vendorMarketplace.ctaPrimary')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={onRegisterClick}
                  className="border-2 border-primary text-primary hover:bg-secondary"
                >
                  {t('landing.vendorMarketplace.ctaSecondary')}
                </Button>
              </div>
            </div>

            {/* Right: Stats & Trust */}
            <div className="space-y-8">
              <div className="relative">
                <div className="rounded-2xl shadow-xl border border-slate-200 w-full bg-primary overflow-hidden aspect-video flex items-center justify-center">
                  {/* Inline illustration: vendor-side view */}
                  <svg viewBox="0 0 480 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                    <rect width="480" height="280" fill="#0A4F3A" />
                    <rect x="24" y="24" width="432" height="232" rx="14" fill="rgba(255,255,255,0.06)" />
                    <rect x="40" y="44" width="200" height="12" rx="5" fill="rgba(255,255,255,0.7)" />
                    <rect x="40" y="64" width="140" height="8" rx="4" fill="rgba(255,255,255,0.3)" />
                    {[0,1,2].map((i) => (
                      <g key={i}>
                        <rect x="40" y={92 + i*52} width="396" height="40" rx="8" fill="rgba(255,255,255,0.06)" />
                        <rect x="56" y={102 + i*52} width="100" height="8" rx="3" fill="rgba(232,245,239,0.7)" />
                        <rect x="56" y={114 + i*52} width="72" height="6" rx="3" fill="rgba(232,245,239,0.3)" />
                        <rect x={320 - i*8} y={102 + i*52} width={60 + i*10} height="8" rx="4" fill={i === 0 ? '#E8F5EF' : 'rgba(232,245,239,0.4)'} opacity={i === 0 ? 0.9 : 0.5} />
                        <rect x="400" y={99 + i*52} width="28" height="16" rx="5" fill={i === 0 ? '#E8F5EF' : 'rgba(232,245,239,0.15)'} />
                      </g>
                    ))}
                  </svg>
                </div>
              </div>

              <div className="space-y-6 pt-8">
                <h4 className="text-2xl font-bold text-slate-900">
                  {t('landing.vendorMarketplace.whyVendorsLove')}
                </h4>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Target className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-slate-900 mb-1">
                        {t('landing.vendorMarketplace.benefit1.title')}
                      </h5>
                      <p className="text-sm text-slate-600">
                        {t('landing.vendorMarketplace.benefit1.description')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-slate-900 mb-1">
                        {t('landing.vendorMarketplace.benefit2.title')}
                      </h5>
                      <p className="text-sm text-slate-600">
                        {t('landing.vendorMarketplace.benefit2.description')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-slate-900 mb-1">
                        {t('landing.vendorMarketplace.benefit3.title')}
                      </h5>
                      <p className="text-sm text-slate-600">
                        {t('landing.vendorMarketplace.benefit3.description')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-secondary rounded-xl p-6 border border-primary/20">
                  <p className="text-sm text-primary italic font-medium">
                    "{t('landing.vendorMarketplace.testimonial.quote')}"
                  </p>
                  <div className="flex items-center space-x-3 mt-4">
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <p className="font-semibold text-primary text-sm">
                        {t('landing.vendorMarketplace.testimonial.author')}
                      </p>
                      <p className="text-xs text-primary/70">
                        {t('landing.vendorMarketplace.testimonial.role')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VendorMarketplaceSection;
